import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatDialogModule } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { FormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { DateAdapter, MatNativeDateModule } from "@angular/material/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ApiService } from "app/services/api.service";
import { AlarmService } from "app/services/alarm.service";
import { HelperService, UiLang } from "app/services/helper.service";
import { EmaacAlertHistoryItem } from "app/models/emaac-alert-history.model";
import { IAlarmReasonTranslations } from "app/models/alarm-reasons.model";
import { ReportPipesModule } from "app/components/report/report-pipes";
import { TextFormatComponent } from "app/components/text-format/text-format.component";
import { CapitalizePipe, MomentPipe, TranslateAlarmCategoryPipe, TranslateAlarmReasonPipe } from "app/pipes/pipes";
import { TimeInputDirective } from "app/directives/time-input.directive";
import { PhoneTextComponent } from "./phone-text/phone-text.component";
import { PhonebookButtonComponent } from "../phonebook/phonebook-button/phonebook-button.component";
import { IOption, MultiselectDropdownComponent } from "app/components/multiselect-dropdown/multiselect-dropdown.component";
import {
  EmaacHistoryFilters,
  EmaacHistoryRow,
  EmaacHistoryValueColumn,
  combineDateAndTime,
  filterAlerts,
  getColumnOptions,
} from "./emaac-alert-history-filter";

/**
 * EMAAC Alert History: the latest completed alerts handled through EMAAC, opened as a dialog
 * from the EMAAC navigation. Same layout as the Portal report alert table: click a row for details.
 */
@Component({
  selector: "app-emaac-alert-history",
  templateUrl: "./emaac-alert-history.component.html",
  styleUrls: ["../../report/report.component.css", "./emaac-alert-history.component.css"],
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
    ReportPipesModule,
    TextFormatComponent,
    MomentPipe,
    TranslateAlarmCategoryPipe,
    TranslateAlarmReasonPipe,
    CapitalizePipe,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MultiselectDropdownComponent,
    TimeInputDirective,
    PhoneTextComponent,
    PhonebookButtonComponent,
  ],
})
export class EmaacAlertHistoryComponent implements OnInit {
  /** How many alerts the backend returns (EmaacAlertHistoryService.MaxAlerts) */
  readonly maxAlerts = 50;
  alerts: EmaacHistoryRow[] = [];
  /** Alerts shown in the table: the loaded alerts that match the filters */
  filteredAlerts: EmaacHistoryRow[] = [];
  loading = false;
  loadError = false;
  alarmReasonTranslations: IAlarmReasonTranslations = { alarmCategory: [], alarmReason: [] };

  // Filters. Column options are the values present in the loaded alerts.
  filters = new EmaacHistoryFilters();
  options: Record<EmaacHistoryValueColumn, IOption[]> = { customer: [], area: [], device: [], handledBy: [] };
  fromDate: Date | null = null;
  /** Empty means 00:00 (shown as placeholder) */
  fromTime = "";
  toDate: Date | null = null;
  /** Empty means 23:59 (shown as placeholder) */
  toTime = "";

  constructor(
    private api: ApiService,
    private alarmService: AlarmService,
    private translate: TranslateService,
    private helper: HelperService,
    private dateAdapter: DateAdapter<any>
  ) {}

  ngOnInit(): void {
    const lang = (this.translate.currentLang || "en") as UiLang;
    this.helper.setDateAdapterLocale(lang, this.dateAdapter);
    this.alarmService.getAlarmReasonTranslations(lang).subscribe({
      next: (translations) => (this.alarmReasonTranslations = translations),
      error: (err) => console.error("Failed to load alarm reason translations", err),
    });

    this.loadAlerts();
  }

  /** Load the history; also the header's Refresh button. The current list stays visible until new data arrives. */
  loadAlerts(): void {
    this.loading = true;
    this.loadError = false;
    this.api.getEmaacAlertHistory().subscribe({
      next: (alerts) => {
        const noOneLoggedIn = this.translate.instant("LOG_NO_ONLINE_RECIPIENTS");
        this.alerts = (alerts ?? []).map((alert) => ({
          ...alert,
          handledByName: alert.handledBy?.userName || noOneLoggedIn,
        }));
        this.updateOptions();
        this.applyFilters();
        this.loading = false;
      },
      error: (err) => {
        console.error("Failed to load EMAAC alert history", err);
        this.alerts = [];
        this.filteredAlerts = [];
        this.loadError = true;
        this.loading = false;
      },
    });
  }

  /** Dropdown options from the loaded alerts. Selections stay, also when a value is no longer in the data. */
  private updateOptions(): void {
    for (const column of Object.keys(this.options) as EmaacHistoryValueColumn[]) {
      this.options[column] = getColumnOptions(this.alerts, column);
    }
  }

  applyFilters(): void {
    this.filters.from = combineDateAndTime(this.fromDate, this.fromTime, "00:00");
     this.filters.to = combineDateAndTime(this.toDate, this.toTime, "23:59")?.endOf("minute") ?? null;
    this.filteredAlerts = filterAlerts(this.alerts, this.filters);
  }

  clearFilters(): void {
    this.filters = new EmaacHistoryFilters();
    this.fromDate = null;
    this.fromTime = "";
    this.toDate = null;
    this.toTime = "";
    this.applyFilters();
  }

  expandRow(alert: EmaacHistoryRow): void {
    alert.expanded = !alert.expanded;
  }

  /** Street, post code and post office joined, skipping missing parts */
  getAddress(alert: EmaacAlertHistoryItem): string {
    const city = [alert.postCode, alert.postOffice].filter((p) => !!p).join(" ");
    return [alert.address, city].filter((p) => !!p).join(", ");
  }
}
